import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types/auth';
import { authService, AuthenticationError } from '../services/authService';
import {
  storeAuthData,
  getStoredUser,
  getStoredToken,
  clearAuthData,
  isAuthenticated as checkIsAuthenticated,
  isTokenExpired,
} from '../utils/authStorage';

/**
 * Authentication context type
 */
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (name: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
}

/**
 * Auth context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth provider props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth provider component
 * Manages authentication state and provides auth methods to the app
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth state from storage on mount
  useEffect(() => {
    const initAuth = () => {
      try {
        // Check if token is expired
        if (isTokenExpired()) {
          clearAuthData();
          setUser(null);
          setIsLoading(false);
          return;
        }

        // Validate stored auth data
        if (checkIsAuthenticated()) {
          const storedUser = getStoredUser();
          const token = getStoredToken();

          if (storedUser && token) {
            setUser(storedUser);
          } else {
            clearAuthData();
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login user
   * @returns true if successful, false otherwise
   */
  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: AuthResponse = await authService.login(email, password);
      
      // Store auth data
      storeAuthData(response);
      setUser(response.user);
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof AuthenticationError 
        ? err.message 
        : 'Login failed. Please try again.';
      
      console.error('Login error:', err);
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Register new user
   * @returns true if successful, false otherwise
   */
  const register = async (
    name: string,
    email: string,
    password: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response: AuthResponse = await authService.register(name, email, password);
      
      // Store auth data
      storeAuthData(response);
      setUser(response.user);
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof AuthenticationError 
        ? err.message 
        : 'Registration failed. Please try again.';
      
      console.error('Registration error:', err);
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user
   * Clears all auth data from storage and state
   */
  const logout = () => {
    clearAuthData();
    setUser(null);
    setError(null);
  };

  /**
   * Clear error message
   */
  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null && checkIsAuthenticated(),
    isLoading,
    error,
    login,
    register,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 * Must be used within AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
